type Keyword = string | string[] | null | undefined

/**
 * Scores a command menu entry by checking whether every trimmed search term
 * appears somewhere in the command value or its associated keywords.
 *
 * The filter is intentionally loose:
 * - punctuation and casing are ignored for both search terms and targets
 * - terms can match anywhere within the combined text
 * - if no direct match is found, terms can match the start of any individual token
 *
 * Returns `1` for a match (keep the item) and `0` otherwise.
 */
export const commandMenuFilter = (
  value: string,
  search: string,
  keywords: Keyword[] = [],
): number => {
  const normalizedSearch = search.trim().toLowerCase()
  if (normalizedSearch === '') return 1

  const searchTerms = normalizedSearch
    .split(/\s+/)
    .map((term) => term.replace(/[^a-z0-9]+/gi, '').trim())
    .filter(Boolean)

  if (searchTerms.length === 0) return 1

  const normalizedParts = [value, ...keywords]
    .flat()
    .filter(
      (part): part is string =>
        typeof part === 'string' && part.trim().length > 0,
    )
    .map((part) =>
      part
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, ' ')
        .trim(),
    )
    .filter(Boolean)

  if (normalizedParts.length === 0) return 0

  const targetText = normalizedParts.join(' ')
  const targetTokens = normalizedParts.flatMap((segment) =>
    segment.split(/\s+/).filter(Boolean),
  )

  const matchesEveryTerm = searchTerms.every((term) => {
    if (targetText.includes(term)) return true
    return targetTokens.some((token) => token.startsWith(term))
  })

  return matchesEveryTerm ? 1 : 0
}
